/* eslint-disable indent */
import React, { useEffect } from "react";
import styles from "../EditableTag.module.css";
import Seta from "../../../Assets/images/seta.png";
import { useEditableTag } from "./EditableTagBase";
import { ToolbarComponent, CanvasComponent } from "./SharedComponents";

const EditableTag16x5 = ({ initialData, doubleTag = false }) => {
	const canvasSize = { width: 453.54, height: 141.66 };
	const {
		canvasRef,
		elements,
		setElements,
		setInitialElements,
		selectedElement,
		setSelectedElement,
		zoom,
		handleMouseDown,
		handleElementClick,
		handleMouseMove,
		handleMouseUp,
		updateElementProperty,
		deleteElement,
		handleZoomIn,
		handleZoomOut,
		handleZoomReset,
		exportToPDF,
		printPDF,
		resetToInitialState
	} = useEditableTag(canvasSize);

	useEffect(() => {
		if (initialData)
			initializeElements(initialData);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialData]);

	const initializeElements = (data) => {
		const newElements = [];
		let idCounter = Date.now();

		// Layout 16x5: Top com logo, cultura+barcode e QR Code
		const topHeight = 40;
		const bottomY = topHeight + 18;

		const logoX = 10;
		const logoWidth = 70;

		const centerX = logoX + logoWidth + 10;
		const centerWidth = 250;

		// Cultura centralizada no topo (meio da etiqueta, entre logo e lado direito)
		if (data.culture) {
			newElements.push({
				id: idCounter++,
				type: "text",
				content: data.culture,
				x: centerX,
				y: 8,
				width: centerWidth,
				height: 15,
				fontSize: 12,
				fontWeight: "bold",
				fontFamily: "Roboto, Arial, sans-serif",
				textAlign: "center",
				textTransform: "uppercase",
				overflow: "visible"
			});
		}

		// Código de Barras CODE128 - centralizado abaixo da cultura (dentro do codBarContainer)
		if (data.barCode) {
			newElements.push({
				id: idCounter++,
				type: "image",
				content: data.barCode,
				x: centerX + 15,
				y: 23,
				width: centerWidth - 30,
				height: 30,
				objectFit: "contain"
			});
		}

		// QR Code no canto superior direito (absoluto)
		if (data.qrCode) {
			newElements.push({
				id: idCounter++,
				type: "image",
				content: data.qrCode,
				x: canvasSize.width - 50,
				y: 5,
				width: 40,
				height: 40
			});
		}

		// LADO DIREITO: Peso + Código de Barras PLU
		const rightSideX = canvasSize.width - 155;

		// BOTTOM CONTAINER - Informações textuais à esquerda
		const leftColumnX = 10;
		const leftColumnWidth = 300;
		let currentY = bottomY;

		// Detectar se há muito conteúdo (distribuidor + data de validade)
		const hasDistributor = !!data.distributor;
		const hasExpiryDate = !!(data.dates && data.dates.expiry);
		const hasHeavyContent = hasDistributor && hasExpiryDate;

		// Ajustar fontSize e espaçamento baseado na quantidade de conteúdo
		const baseFontSize = hasHeavyContent ? 8 : 10;
		const lineSpacing = hasHeavyContent ? 8 : 10;

		// Código de Rastreio
		if (data.trackingCode) {
			newElements.push({
				id: idCounter++,
				type: "text",
				content: `Código de Rastreio: ${data.trackingCode}`,
				x: leftColumnX,
				y: currentY,
				width: leftColumnWidth,
				height: lineSpacing,
				fontSize: baseFontSize,
				fontWeight: "bold",
				fontFamily: "Roboto, Arial, sans-serif",
				textAlign: "left"
			});
			currentY += lineSpacing;
		}

		// Produtor
		if (data.producer) {
			const producerLine1 = `Distribuído por ${data.producer.name} ${data.producer.document}`;
			const producerLine2 = data.producer.address;
			newElements.push({
				id: idCounter++,
				type: "text",
				content: producerLine1,
				x: leftColumnX,
				y: currentY,
				width: leftColumnWidth,
				height: lineSpacing,
				fontSize: baseFontSize,
				fontWeight: "bold",
				fontFamily: "Roboto, Arial, sans-serif",
				textAlign: "left"
			});
			currentY += lineSpacing;
			newElements.push({
				id: idCounter++,
				type: "text",
				content: producerLine2,
				x: leftColumnX,
				y: currentY,
				width: leftColumnWidth,
				height: lineSpacing,
				fontSize: baseFontSize,
				fontWeight: "bold",
				fontFamily: "Roboto, Arial, sans-serif",
				textAlign: "left"
			});
			currentY += lineSpacing;
		}

		// Distribuidor
		if (data.distributor) {
			const distributorLine1 = `Destino ${data.distributor.name} ${data.distributor.document}`;
			const distributorLine2 = data.distributor.address;
			newElements.push({
				id: idCounter++,
				type: "text",
				content: distributorLine1,
				x: leftColumnX,
				y: currentY,
				width: leftColumnWidth,
				height: lineSpacing,
				fontSize: baseFontSize,
				fontWeight: "bold",
				fontFamily: "Roboto, Arial, sans-serif",
				textAlign: "left"
			});
			currentY += lineSpacing;
			newElements.push({
				id: idCounter++,
				type: "text",
				content: distributorLine2,
				x: leftColumnX,
				y: currentY,
				width: leftColumnWidth,
				height: lineSpacing,
				fontSize: baseFontSize,
				fontWeight: "bold",
				fontFamily: "Roboto, Arial, sans-serif",
				textAlign: "left"
			});
			currentY += lineSpacing;
		}

		// Datas - adaptável para múltiplas linhas quando necessário
		if (data.dates) {
			// Se houver data de validade, dividir em duas linhas para evitar sobreposição
			if (data.dates.expiry) {
				// Primeira linha: Data de Embalagem e Data de Entrega
				let dateText1 = "";
				if (data.dates.packaging) dateText1 += `Data de Embalagem: ${data.dates.packaging} - `;
				if (data.dates.delivery) dateText1 += `Data de Entrega: ${data.dates.delivery}`;

				newElements.push({
					id: idCounter++,
					type: "text",
					content: dateText1,
					x: leftColumnX,
					y: currentY,
					width: leftColumnWidth,
					height: lineSpacing,
					fontSize: baseFontSize,
					fontWeight: "bold",
					fontFamily: "Roboto, Arial, sans-serif",
					textAlign: "left",
					overflow: "visible",
					lineHeight: 1.0
				});
				currentY += lineSpacing;

				// Segunda linha: Data de Validade
				newElements.push({
					id: idCounter++,
					type: "text",
					content: `Data de Validade: ${data.dates.expiry}`,
					x: leftColumnX,
					y: currentY,
					width: leftColumnWidth,
					height: lineSpacing,
					fontSize: baseFontSize,
					fontWeight: "bold",
					fontFamily: "Roboto, Arial, sans-serif",
					textAlign: "left",
					overflow: "visible",
					lineHeight: 1.0
				});
				currentY += lineSpacing;
			} else {
				// Sem data de validade - tudo em uma linha
				let dateText = "";
				if (data.dates.packaging) dateText += `Data de Embalagem: ${data.dates.packaging} - `;
				if (data.dates.delivery) dateText += `Data de Entrega: ${data.dates.delivery}`;

				newElements.push({
					id: idCounter++,
					type: "text",
					content: dateText,
					x: leftColumnX,
					y: currentY,
					width: leftColumnWidth,
					height: lineSpacing,
					fontSize: baseFontSize,
					fontWeight: "bold",
					fontFamily: "Roboto, Arial, sans-serif",
					textAlign: "left",
					overflow: "visible",
					lineHeight: 1.0
				});
				currentY += lineSpacing;
			}
		}

		// Especificação (se houver)
		if (data.specification) {
			newElements.push({
				id: idCounter++,
				type: "text",
				content: data.specification,
				x: leftColumnX,
				y: currentY,
				width: leftColumnWidth,
				height: 10,
				fontSize: 10,
				fontWeight: "bold",
				fontFamily: "Roboto, Arial, sans-serif",
				textAlign: "left"
			});
		}

		// Peso Líquido - no lado direito, abaixo do QR Code, em uma única linha
		if (data.weight) {
			const pesoY = 50;

			// Texto completo "Peso liq. " + valor em uma única linha
			newElements.push({
				id: idCounter++,
				type: "text",
				content: `Peso liq. ${data.weight}`,
				x: rightSideX + 15,
				y: pesoY,
				width: 135,
				height: 16,
				fontSize: 14,
				fontWeight: "bold",
				fontFamily: "Roboto, Arial, sans-serif",
				textAlign: "center",
				overflow: "visible"
			});
		}

		// Código de Barras EAN13/PLU (abaixo do peso, lado direito)
		if (data.barCodePLU) {
			newElements.push({
				id: idCounter++,
				type: "image",
				content: data.barCodePLU,
				x: rightSideX + 2,
				y: 68,
				width: 150,
				height: 50,
				objectFit: "contain"
			});
		}

		// Footer
		const footerY = 129;
		const footerHeight = 10;

		// Fundo preto do footer
		newElements.push({
			id: idCounter++,
			type: "footer-background",
			content: "",
			x: 0,
			y: footerY,
			width: canvasSize.width,
			height: footerHeight,
			backgroundColor: "black",
			isFooterElement: true,
			zIndex: -1
		});

		// Layout do footer
		const footerPadding = 8;
		const arrowWidth = 10;
		const spacing = 8;
		const totalArrows = 3 * arrowWidth;
		const totalSpacing = 4 * spacing;
		const availableForTexts = canvasSize.width - totalArrows - totalSpacing - (2 * footerPadding);
		const textWidth = availableForTexts / 2;

		let currentX = footerPadding;

		// Seta 1
		newElements.push({
			id: idCounter++,
			type: "image",
			content: Seta,
			x: currentX,
			y: footerY + 1,
			width: arrowWidth,
			height: 8,
			isFooterElement: true
		});
		currentX += arrowWidth + spacing;

		// Texto 1
		newElements.push({
			id: idCounter++,
			type: "text",
			content: "PRODUTO COM ORIGEM RASTREADA",
			x: currentX,
			y: footerY + 2,
			width: textWidth,
			height: 8,
			fontSize: 8,
			fontWeight: "bold",
			fontFamily: "Roboto, Arial, sans-serif",
			textAlign: "center",
			color: "white",
			lineHeight: 1.0,
			overflow: "visible",
			isFooterElement: true
		});
		currentX += textWidth + spacing;

		// Seta 2 (invertida)
		newElements.push({
			id: idCounter++,
			type: "image",
			content: Seta,
			x: currentX,
			y: footerY + 1,
			width: arrowWidth,
			height: 8,
			transform: "scaleX(-1)",
			isFooterElement: true
		});
		currentX += arrowWidth + spacing;

		// Texto 2
		newElements.push({
			id: idCounter++,
			type: "text",
			content: "https://www.sisagri.com.br",
			x: currentX,
			y: footerY + 2,
			width: textWidth,
			height: 8,
			fontSize: 8,
			fontWeight: "bold",
			fontFamily: "Roboto, Arial, sans-serif",
			textAlign: "center",
			color: "white",
			lineHeight: 1.0,
			overflow: "visible",
			isFooterElement: true
		});
		currentX += textWidth + spacing;

		// Seta 3 (invertida)
		newElements.push({
			id: idCounter++,
			type: "image",
			content: Seta,
			x: currentX,
			y: footerY + 1,
			width: arrowWidth,
			height: 8,
			transform: "scaleX(-1)",
			isFooterElement: true
		});

		setElements(newElements);
		setInitialElements(JSON.parse(JSON.stringify(newElements)));
	};

	const selectedElementData = elements.find(el => el.id === selectedElement);

	return (
		<div className={styles.editorContainer}>
			<ToolbarComponent
				selectedElementData={selectedElementData}
				updateElementProperty={updateElementProperty}
				handleZoomOut={handleZoomOut}
				handleZoomIn={handleZoomIn}
				handleZoomReset={handleZoomReset}
				zoom={zoom}
				exportToPDF={() => exportToPDF("16x5", doubleTag)}
				printPDF={() => printPDF(doubleTag)}
				resetToInitialState={resetToInitialState}
			/>

			<CanvasComponent
				canvasRef={canvasRef}
				canvasSize={canvasSize}
				zoom={zoom}
				elements={elements}
				selectedElement={selectedElement}
				setSelectedElement={setSelectedElement}
				handleMouseMove={handleMouseMove}
				handleMouseUp={handleMouseUp}
				handleMouseDown={handleMouseDown}
				handleElementClick={handleElementClick}
				deleteElement={deleteElement}
			/>
		</div>
	);
};

export default EditableTag16x5;
